import { useState } from "react";
import { Heart, Shield, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// PayPal donation links - Sickle Cell Association
const PAYPAL_DONATION_EMAIL = "sicklecellhouston@gmail.com";

const buildOneTimeUrl = (amount) => {
  const params = new URLSearchParams({
    business: PAYPAL_DONATION_EMAIL,
    currency_code: "USD",
    item_name: "Donation to Sickle Cell Association",
  });
  if (amount) params.set("amount", String(amount));
  return `https://www.paypal.com/donate/?${params.toString()}`;
};

// PayPal standard "Subscribe" URL (cmd=_xclick-subscriptions) — recurring monthly
const buildMonthlyUrl = (amount) => {
  const params = new URLSearchParams({
    cmd: "_xclick-subscriptions",
    business: PAYPAL_DONATION_EMAIL,
    item_name: "Monthly Donation to Sickle Cell Association",
    currency_code: "USD",
    a3: String(amount || 0), // amount per period
    p3: "1", // period count
    t3: "M", // period unit: M = Month
    src: "1", // recurring
    sra: "1", // reattempt on failure
    no_note: "1",
    no_shipping: "1",
  });
  return `https://www.paypal.com/cgi-bin/webscr?${params.toString()}`;
};

const DonatePage = () => {
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [frequency, setFrequency] = useState("once"); // "once" | "monthly"

  const donationAmounts = [
    { value: 25, label: "$25", description: "Supporter" },
    { value: 50, label: "$50", description: "Advocate" },
    { value: 100, label: "$100", description: "Champion" },
    { value: 250, label: "$250", description: "Hero" },
  ];

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmount = (e) => {
    const value = e.target.value;
    setCustomAmount(value);
    if (value) {
      setSelectedAmount(null);
    }
  };

  const getFinalAmount = () => {
    if (customAmount && parseFloat(customAmount) > 0) {
      return parseFloat(customAmount);
    }
    return selectedAmount;
  };

  const isMonthly = frequency === "monthly";
  const finalAmount = getFinalAmount();
  const paypalUrl = isMonthly ? buildMonthlyUrl(finalAmount) : buildOneTimeUrl(finalAmount);

  return (
    <div className="pt-20" data-testid="donate-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-amber-700 font-medium tracking-widest uppercase text-sm mb-4">
              Support Our Mission
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-slate-900 leading-[1.1] mb-6">
              Make a <span className="text-amber-700 italic">Difference</span> Today
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
              Your generous donation helps us advocate for life-changing legislation, support patients, and raise awareness about sickle cell disease.
            </p>
          </div>
        </div>
      </section>

      {/* Donation Form Section */}
      <section className="py-16 bg-slate-50" data-testid="donation-form-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Card className="shadow-xl border-0">
            <CardContent className="p-8 md:p-12">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Heart className="w-6 h-6 text-amber-700" />
                </div>
                <div>
                  <h2 className="font-serif text-2xl font-semibold text-slate-900">Choose Your Gift</h2>
                  <p className="text-slate-500 text-sm">Select an amount or enter a custom donation</p>
                </div>
              </div>

              {/* Frequency Toggle */}
              <div className="mb-8" data-testid="frequency-toggle-section">
                <div className="inline-flex w-full md:w-auto rounded-lg border border-slate-200 bg-slate-50 p-1">
                  <button
                    type="button"
                    onClick={() => setFrequency("once")}
                    className={`flex-1 md:flex-none px-6 py-2.5 rounded-md font-sans text-sm font-semibold transition-all ${
                      !isMonthly
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid="frequency-once-btn"
                  >
                    One-time
                  </button>
                  <button
                    type="button"
                    onClick={() => setFrequency("monthly")}
                    className={`flex-1 md:flex-none px-6 py-2.5 rounded-md font-sans text-sm font-semibold transition-all ${
                      isMonthly
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid="frequency-monthly-btn"
                  >
                    Monthly
                  </button>
                </div>
                {isMonthly && (
                  <p className="text-teal-700 text-sm mt-3 flex items-start gap-2">
                    <Heart className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Sustained monthly giving powers our year-round advocacy. You can cancel anytime from your PayPal account.
                    </span>
                  </p>
                )}
              </div>

              {/* Amount Selection */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {donationAmounts.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAmountSelect(option.value)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedAmount === option.value
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                    data-testid={`donation-amount-${option.value}`}
                  >
                    <p className="font-serif text-2xl font-bold">{option.label}</p>
                    <p className={`text-xs uppercase tracking-wider ${
                      selectedAmount === option.value ? "text-slate-300" : "text-slate-500"
                    }`}>
                      {option.description}
                    </p>
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div className="mb-8">
                <Label htmlFor="customAmount" className="text-sm font-medium text-slate-700 mb-2 block">
                  Or enter a custom amount
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
                  <Input
                    id="customAmount"
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={handleCustomAmount}
                    className="pl-8 h-12 text-lg"
                    data-testid="custom-amount-input"
                  />
                </div>
              </div>

              {/* Donor Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div>
                  <Label htmlFor="donorName" className="text-sm font-medium text-slate-700 mb-2 block">
                    Name (Optional)
                  </Label>
                  <Input
                    id="donorName"
                    type="text"
                    placeholder="Your name"
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    className="h-12"
                    data-testid="donor-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="donorEmail" className="text-sm font-medium text-slate-700 mb-2 block">
                    Email (Optional)
                  </Label>
                  <Input
                    id="donorEmail"
                    type="email"
                    placeholder="your@email.com"
                    value={donorEmail}
                    onChange={(e) => setDonorEmail(e.target.value)}
                    className="h-12"
                    data-testid="donor-email-input"
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="bg-slate-50 rounded-lg p-6 mb-8">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">
                    {isMonthly ? "Your Monthly Donation" : "Your Donation"}
                  </span>
                  <span className="font-serif text-3xl font-bold text-slate-900">
                    ${finalAmount?.toFixed(2) || "0.00"}
                    {isMonthly && <span className="text-base font-normal text-slate-500">/mo</span>}
                  </span>
                </div>
              </div>

              {/* PayPal Donate Button */}
              <div className="mt-2" data-testid="paypal-payment-section">
                <a
                  href={paypalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  data-testid="paypal-donate-btn"
                >
                  <Button
                    type="button"
                    disabled={!finalAmount}
                    className="w-full bg-[#0070ba] hover:bg-[#003087] text-white py-6 font-sans font-bold tracking-wide uppercase text-sm"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.016-2.587 4.673-5.846 4.673h-2.19c-1.572 0-2.904 1.145-3.15 2.7l-1.12 7.106H2.47a.641.641 0 0 1-.633-.74l.272-1.723h3.346c.524 0 .967-.382 1.05-.9l.87-5.498h1.753c4.298 0 7.664-1.747 8.647-6.797.03-.149.054-.294.077-.437a3.35 3.35 0 0 0 .37-.097z"/>
                    </svg>
                    {isMonthly
                      ? `Subscribe $${finalAmount?.toFixed(2) || "0.00"}/month`
                      : `Donate $${finalAmount?.toFixed(2) || "0.00"} with PayPal`}
                  </Button>
                </a>
                <p className="text-center text-slate-500 text-sm mt-4">
                  {isMonthly
                    ? "You will be redirected to PayPal to set up your recurring monthly donation. Cancel anytime from your PayPal account."
                    : "You will be redirected to PayPal to complete your donation securely. PayPal accepts credit/debit cards too — no PayPal account required."}
                </p>
                {/* PayPal Info */}
                <div className="flex items-center justify-center gap-2 mt-2 text-slate-500 text-sm">
                  <Shield className="w-4 h-4" />
                  <span>Secure payment via PayPal</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-24 bg-white" data-testid="impact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-teal-700 font-medium tracking-widest uppercase text-sm mb-4">
              Your Impact
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-slate-900">
              How Your Gift Helps
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-slate-100">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                  <Check className="w-6 h-6 text-amber-700" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-slate-900 mb-3">Legislative Advocacy</h3>
                <p className="text-slate-600 leading-relaxed">
                  Fund our efforts to pass life-changing legislation for sickle cell patients in Texas.
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                  <Check className="w-6 h-6 text-teal-700" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-slate-900 mb-3">Patient Support</h3>
                <p className="text-slate-600 leading-relaxed">
                  Provide resources, education, and direct support to patients and their families.
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-6">
                  <Check className="w-6 h-6 text-slate-700" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-slate-900 mb-3">Community Outreach</h3>
                <p className="text-slate-600 leading-relaxed">
                  Expand awareness programs and educational events throughout the Houston area.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Tax Info */}
      <section className="py-12 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-amber-400 font-medium mb-2">501(c)(3) Nonprofit Organization</p>
          <p className="text-slate-400 text-sm">
            Your donation is tax-deductible to the fullest extent allowed by law. You will receive a receipt for your records.
          </p>
        </div>
      </section>
    </div>
  );
};

export default DonatePage;
